/**
 * @file positions.ts
 * @fileoverview Dit bestand bevat helpers voor het werken met formatieposities.
 * Het slaat coördinaten op en biedt hulpprogramma's voor het vertalen van positielabels
 * op basis van de huidige taal.
 */
import { Player, Position } from '../types';
import type { Locale } from '../i18n';
import { getDefaultPositionLabel } from '../constants/positionNames';
import { positionsEquivalent, resolvePositionKey } from './positionKeys';
import { normalizePositionPreferences } from './player';
import { hockeyPositionRegistry, getHockeyPositionDisplayReference, getHockeyPositionNames, getReviewedHockeyPositionLabel, matchHockeyFormationContext, type HockeyFormationDisplayContext } from '@waedo/frontend-core/hockey-positions';

// Keep display context outside serialised formation JSON, including copies of a parsed map.
const sourceDisplayContexts = new WeakMap<object, HockeyFormationDisplayContext>();
const parsedDisplayContexts = new WeakMap<object, string | undefined>();
const positionDisplayContexts = new WeakMap<object, string | undefined>();

export const registerFormationPositionDisplayContext = (positions: unknown, context: HockeyFormationDisplayContext): void => {
  if (positions && typeof positions === 'object') sourceDisplayContexts.set(positions, context);
};

export interface FormationLayoutMetaField {
  /** Custom background color for the rendered field. */
  backgroundColor?: string;
  /** Key of the match type that should be used for the field layout. */
  key?: string;
}

/**
 * Additional layout metadata that can be stored alongside formation positions.
 */
export interface FormationLayoutMeta {
  field?: FormationLayoutMetaField;
}

export type FormationPositionsMap = Record<string, FormationPosition> & {
  __meta?: FormationLayoutMeta;
};

/** Resolve saved preferences to the actual slots in the selected formation. */
export const createFormationPreferenceResolver = (positions: FormationPositionsMap, slots: string[]) => {
  const slotsByReference = new Map<string, string[]>();
  for (const slot of slots) {
    const node = positions[slot];
    const reference = getHockeyPositionDisplayReference(slot, node, positionDisplayContexts.get(node));
    if (!reference) continue;
    const matchingSlots = slotsByReference.get(reference) ?? [];
    matchingSlots.push(slot);
    slotsByReference.set(reference, matchingSlots);
  }

  return (key: string): string | undefined => {
    if (Object.prototype.hasOwnProperty.call(positions, key)) return key;
    if (hockeyPositionRegistry.ambiguousLegacyKeys.includes(key)) return undefined;
    const reference = getHockeyPositionDisplayReference(key, {});
    const matchingSlots = reference ? slotsByReference.get(reference) : undefined;
    return matchingSlots?.length === 1 ? matchingSlots[0] : resolvePositionKey(key, slots);
  };
};

/**
 * Interface voor een formatiepositie.
 */
export interface FormationPosition {
  /** De x-coördinaat van de positie op het veld (in procent). */
  x: number;
  /** De y-coördinaat van de positie op het veld (in procent). */
  y: number;
  /** De sorteervolgorde van de positie. */
  order?: number;
  /** Het maximale aantal spelers op deze positie. */
  max_players?: number;
  /** Het minimale aantal spelers op deze positie. */
  min_players?: number;
  /** Het vereiste aantal spelers op deze positie. */
  required_players?: number;
  /** Een object met vertalingen voor het label van de positie. */
  label_translations?: Record<string, string>;
  /** Geeft aan of de positie vergrendeld is. */
  locked?: boolean;
  /**
   * Positie-index binnen de lijn. Begint bij 1.
   * Wordt niet weergegeven in de UI, alleen gebruikt voor interne sortering.
   */
  line_position_number?: number;
  /** Andere eigenschappen. */
  [key: string]: any;
}

/**
 * Haalt het label voor een positie op in de juiste taal.
 *
 * @param positions - Een object met de posities.
 * @param position - De sleutel van de positie.
 * @param lang - De taalcode (bijv. 'nl' of 'en').
 * @returns Het vertaalde label van de positie.
 */
export const getPositionLabel = (
  positions: Record<string, FormationPosition | string | Record<Locale, string>> | undefined,
  position: string,
  lang?: Locale
): string => {
  const locale: Locale =
    lang || (document.documentElement.lang === 'en' ? 'en' : 'nl');
  if (!position) return '';

  const resolvedKey = resolvePositionKey(position, Object.keys(positions || {}));
  const pos = resolvedKey ? positions?.[resolvedKey] : undefined;
  if (!pos) return getDefaultPositionLabel(position, locale);
  const matchedFormationKey = parsedDisplayContexts.has(positions!) ? parsedDisplayContexts.get(positions!)
    : typeof pos === 'object' && positionDisplayContexts.has(pos) ? positionDisplayContexts.get(pos)
      : parsedDisplayContexts.get(parseFormationPositions(positions).flat);
  const reference = getHockeyPositionDisplayReference(resolvedKey || position, typeof pos === 'object' ? pos : {}, matchedFormationKey);
  const accepted = reference && getHockeyPositionNames(reference)?.[locale];
  if (accepted) return accepted;
  const label = (value: string) => getReviewedHockeyPositionLabel(resolvedKey || position, value, locale, matchedFormationKey);
  if (typeof pos === 'string') return label(pos);
  if ((pos as FormationPosition).label_translations) {
    const translations = (pos as FormationPosition).label_translations!;
    return translations[locale] ? label(translations[locale]) : translations['nl'] || getDefaultPositionLabel(position, locale);
  }
  if (typeof pos === 'object') {
    const obj = pos as Record<Locale, string>;
    if (obj[locale]) return label(obj[locale]);
    if (obj['nl']) return obj['nl'];
  }
  return getDefaultPositionLabel(position, locale);
};

/**
 * Een lijst met verouderde positie-afkortingen die niet langer gebruikt worden.
 */
export const OLD_POSITIONS = [
  'AM', 'CB', 'CM', 'GK', 'LB', 'LW', 'RB', 'RW', 'CDM', 'ST1', 'ST2'
] as const;

/**
 * Controleert of een positie een verouderde positie is.
 *
 * @param position - De positie-afkorting.
 * @returns `true` als de positie verouderd is, anders `false`.
 */
export const isOldPosition = (position: string): boolean => {
  return OLD_POSITIONS.includes(position as typeof OLD_POSITIONS[number]);
};

/**
 * Filtert de verouderde posities uit een posities-object.
 *
 * @param positions - Het object met posities om te filteren.
 * @returns Een nieuw object met alleen de niet-verouderde posities.
 */
export const filterOldPositions = (
  positions: Record<string, FormationPosition>
): Record<string, FormationPosition> => {
  const filtered: Record<string, FormationPosition> = {};
  Object.entries(positions).forEach(([key, value]) => {
    if (!isOldPosition(key)) {
      filtered[key] = value;
    }
  });
  return filtered;
};

/**
 * Bepaalt de prioriteit van een speler voor een gegeven positie.
 *
 * @param player - De speler waarvan de prioriteit wordt opgehaald.
 * @param position - De positie waarvoor de prioriteit nodig is.
 * @returns De prioriteit of `null` als de speler de positie niet prefereert.
 */
export const getPlayerPositionPriority = (
  player: Player,
  position: Position
): number | null => {
  return (
    normalizePositionPreferences(player.positionPreferences).preferences
      .find(pp => positionsEquivalent(pp.position, position))?.priority ?? null
  );
};

/**
 * Interface voor een lijn in een formatiehiërarchie.
 */
export interface FormationHierarchyLine {
  /** De unieke sleutel van de lijn. */
  key: string;
  /** Een object met vertalingen voor het label van de lijn. */
  label: Record<string, string>;
  /** Een array met de posities in deze lijn. */
  positions: string[];
  /**
   * Afgeleide positiesleutels gebaseerd op de lijnsleutel.
   * Elke invoer komt overeen met de overeenkomstige index in `positions`.
   */
  numberedKeys: string[];
  /** De sorteervolgorde van de lijn. */
  order?: number;
  /** De y-coördinaat van de lijn. */
  y?: number;
  /** Het maximale aantal spelers in deze lijn. */
  max_players?: number;
  /** Het minimale aantal spelers in deze lijn. */
  min_players?: number;
  /** Het vereiste aantal spelers in deze lijn. */
  required_players?: number;
}

/**
 * Interface voor een geparste formatie.
 */
export interface ParsedFormation {
  /** Een platte weergave van alle posities. */
  flat: Record<string, FormationPosition>;
  /** Een array met de lijnen in de formatie. */
  lines: FormationHierarchyLine[];
}

/**
 * Parset een formatieposities-object naar een `ParsedFormation` object.
 * Dit maakt het makkelijker om met de formatiedata te werken.
 *
 * @param positions - Het te parsen posities-object.
 * @returns Een `ParsedFormation` object.
 */
export const parseFormationPositions = (
  positions: unknown,
  displayContext?: HockeyFormationDisplayContext,
): ParsedFormation => {
  const flat: Record<string, FormationPosition> = {};
  const lines: FormationHierarchyLine[] = [];

  if (!positions || typeof positions !== 'object') return { flat, lines };

  const IGNORED_KEYS = new Set(['__meta']);
  const entries = Object.entries(positions).filter(([key]) => !IGNORED_KEYS.has(key));
  const hierarchical = entries.some(
    ([, val]) => typeof val === 'object' && val !== null && 'line_label' in val
  );

  const finish = (): ParsedFormation => {
    const context = displayContext ?? sourceDisplayContexts.get(positions);
    const matched = matchHockeyFormationContext(lines.flatMap(line => line.positions.map(key => ({
      key, lineKey: hierarchical ? line.key : null,
    }))), context);
    parsedDisplayContexts.set(flat, matched);
    for (const line of lines) for (const key of line.positions) positionDisplayContexts.set(flat[key], matched);
    return { flat, lines };
  };

  if (!hierarchical) {
    entries.forEach(([key, val]) => {
      if (val && typeof val === 'object') {
        flat[key] = { ...val } as FormationPosition;
      }
    });
    lines.push({
      key: 'positions',
      label: { nl: 'Posities', en: 'Positions' },
      positions: Object.keys(flat),
      numberedKeys: []
    });
    return finish();
  }

  entries.forEach(([lineKey, lineVal]) => {
    if (!lineVal || typeof lineVal !== 'object') return;
    const obj = lineVal as any;
    const labelTranslations =
      obj.line_label_translations ||
      (obj.line_label
        ? { nl: obj.line_label, en: obj.line_label }
        : { nl: lineKey, en: lineKey });
    const subPositions: string[] = [];
    const numberedKeys: string[] = [];

    Object.entries(obj).forEach(([posKey, posVal]) => {
      if (
        posKey === 'line_label' ||
        posKey === 'line_label_translations' ||
        posKey.startsWith('line_') || posKey === 'order' || !posVal || typeof posVal !== 'object'
      )
        return;
      const position = { ...(posVal as FormationPosition) };
      position.line_position_number = subPositions.length + 1;
      const numberedKey = `${lineKey}_${position.line_position_number}`;
      flat[posKey] = position;
      subPositions.push(posKey);
      numberedKeys.push(numberedKey);
    });

    const linePos: FormationPosition = {
      x: 50,
      y: obj.line_y ?? 0,
      order: obj.line_order ?? obj.order,
      max_players: obj.line_max_players,
      min_players: obj.line_min_players,
      required_players: obj.line_required_players,
      label_translations: labelTranslations
    };

    flat[lineKey] = linePos;
    lines.push({
      key: lineKey,
      label: labelTranslations,
      positions: subPositions,
      numberedKeys,
      order: obj.line_order ?? obj.order,
      y: obj.line_y,
      max_players: obj.line_max_players,
      min_players: obj.line_min_players,
      required_players: obj.line_required_players
    });
  });

  lines.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return finish();
};

/**
 * Extracts the background color for a formation field from the positions meta object.
 *
 * @param positions - The formation positions object that may contain meta information.
 * @param fallback - Optional fallback color when no custom color is defined.
 * @returns The custom background color or the provided fallback.
 */
export const getFormationFieldBackground = (
  positions: FormationPositionsMap | Record<string, any> | undefined,
  fallback?: string
): string | undefined => {
  if (!positions || typeof positions !== 'object') return fallback;
  const meta = (positions as FormationPositionsMap).__meta;
  const custom = meta?.field?.backgroundColor;
  return custom || fallback;
};

/**
 * Bouwt een leeg opstellingsobject op basis van de posities in een formatie.
 *
 * @param positions - Het posities-object van de formatie.
 * @param mode - De modus om de opstelling te bouwen ('positions' of 'lines').
 * @returns Een leeg opstellingsobject met `null` voor elke positie.
 */
export const buildEmptyLineup = (
  positions: Record<string, any> | undefined,
  mode: 'positions' | 'lines' = 'positions'
): Record<string, null> => {
  const lineup: Record<string, null> = {};
  if (positions) {
    const { flat, lines } = parseFormationPositions(positions);
    const filteredPositions = filterOldPositions(flat);
    lines.forEach(line => {
      delete filteredPositions[line.key];
      if (mode === 'lines') {
        line.positions.forEach(pos => delete filteredPositions[pos]);
        line.numberedKeys.forEach(nKey => {
          lineup[nKey] = null;
        });
      } else {
        line.numberedKeys.forEach(nKey => delete filteredPositions[nKey]);
      }
    });
    Object.keys(filteredPositions).forEach(key => {
      lineup[key] = null;
    });
  }
  return lineup;
};

/** Resolve preferences against actual field roles; never expose synthetic line slots. */
export const getFormationPreferenceKeys = (
  preferences: ReadonlyArray<{ position: string }>,
  positions: Record<string, unknown> | undefined
): string[] => {
  const keys = Object.keys(buildEmptyLineup(positions, 'positions'));
  return [...new Set(preferences
    .map(pref => resolvePositionKey(pref.position, keys))
    .filter((key): key is string => key !== undefined))];
};

/** Individual role coverage only: one player may cover more than one role. */
export const getUncoveredFormationPositions = (
  positions: Record<string, unknown> | undefined,
  availablePreferences: ReadonlyArray<ReadonlyArray<{ position: string }>>
): string[] => {
  const covered = new Set(availablePreferences.flatMap(preferences => getFormationPreferenceKeys(preferences, positions)));
  return Object.keys(buildEmptyLineup(positions, 'positions')).filter(key => !covered.has(key));
};

/**
 * Ruimt een opstellingsobject op door alle `null` waardes te verwijderen.
 *
 * @param lineup - Het op te ruimen opstellingsobject.
 * @returns Een nieuw opstellingsobject zonder `null` waardes.
 */
export const cleanLineup = (
  lineup: Record<string, Player | null> | undefined
): Record<string, Player> => {
  if (!lineup) return {};
  
  return Object.entries(lineup)
    .filter(([_, player]) => player !== null)
    .reduce((acc, [key, player]) => ({
      ...acc,
      [key]: player as Player
    }), {});
};

/**
 * Haalt een gesorteerde lijst van positiesleutels op.
 * De sortering volgt de 'order' eigenschap; gelijke waarden volgen de positiesleutel.
 *
 * @param positions - Het posities-object van de formatie.
 * @returns Een gesorteerde array van positiesleutels.
 */
export const getSortedPositionKeys = (
  positions: Record<string, any> | undefined
): string[] => {
  const { flat, lines } = parseFormationPositions(positions);
  const filtered = { ...flat };
  lines.forEach(line => {
    delete filtered[line.key];
  });
  return Object.entries(filtered)
    .sort((a, b) => {
          const posA = a[1];
          const posB = b[1];
          
          // If positions have order property, sort by order
          const orderA = typeof posA === 'object' && 'order' in posA ? (posA as any).order ?? 0 : 0;
          const orderB = typeof posB === 'object' && 'order' in posB ? (posB as any).order ?? 0 : 0;
          if (orderA !== orderB) return orderA - orderB;
          
          // Otherwise sort alphabetically
          return a[0].localeCompare(b[0]);
        })
        .map(([key]) => key);
};

/**
 * Genereert een posities-object op basis van een array van formatielijnen.
 * Dit is handig voor het dynamisch creëren van posities op basis van lijnen.
 *
 * @param lines - Een array van `FormationHierarchyLine` objecten.
 * @returns Een object met de gegenereerde posities.
 */
export const positionsFromLines = (
  lines: FormationHierarchyLine[]
): Record<string, FormationPosition> => {
  const result: Record<string, FormationPosition> = {};
  lines.forEach(line => {
    const count = line.positions.length;
    if (count === 0) return;
    const step = 100 / (count + 1);
    const sanitize = (label: string) => label.replace(/\d+/g, '').trim();
    const baseLabelEn = sanitize(line.label.en || line.key);
    const baseLabelNl = sanitize(line.label.nl || line.key);
    line.positions.forEach((pos, idx) => {
      const key = line.numberedKeys?.[idx] || pos;
      result[key] = {
        x: step * (idx + 1),
        y: line.y ?? 0,
        label_translations: {
          en: baseLabelEn,
          nl: baseLabelNl
        },
        order: Object.keys(result).length,
        line_position_number: idx + 1
      };
    });
  });
  return result;
};

/**
 * Normaliseert een opstellingsobject om consistentie te garanderen tussen verschillende modi (posities/lijnen).
 * Zorgt ervoor dat spelers correct worden toegewezen, ongeacht de representatie van de opstelling.
 *
 * @param lineup - Het opstellingsobject om te normaliseren.
 * @param positions - Het posities-object van de formatie.
 * @param mode - De modus ('positions' of 'lines') waar naartoe genormaliseerd moet worden.
 * @returns Een genormaliseerd opstellingsobject.
 */
export const normalizeLineup = (
  lineup: Record<string, Player | null>,
  positions: Record<string, any> | undefined,
  mode: 'positions' | 'lines'
): Record<string, Player | null> => {
  if (!positions) return lineup;
  const { flat, lines } = parseFormationPositions(positions);
  const result: Record<string, Player | null> = {};

  if (mode === 'lines') {
    lines.forEach(line => {
      line.numberedKeys.forEach((nKey, idx) => {
        const posKey = line.positions[idx];
        const player = lineup[nKey] ?? lineup[posKey] ?? null;
        const priority = player
          ? getPlayerPositionPriority(player, nKey as Position) ??
            getPlayerPositionPriority(player, posKey as Position) ??
            getPlayerPositionPriority(player, line.key as Position)
          : null;
        result[nKey] = player
          ? { ...player, assignedPositionPriority: priority }
          : null;
      });
    });
    return result;
  }

  // positions mode
  lines.forEach(line => {
    line.positions.forEach((pos, idx) => {
      const nKey = line.numberedKeys[idx];
      const player = lineup[pos] ?? lineup[nKey] ?? null;
      const priority = player
        ? getPlayerPositionPriority(player, pos as Position) ??
          getPlayerPositionPriority(player, nKey as Position) ??
          getPlayerPositionPriority(player, line.key as Position)
        : null;
      result[pos] = player
        ? { ...player, assignedPositionPriority: priority }
        : null;
    });
  });

  Object.keys(flat).forEach(key => {
    if (
      lines.some(
        l => l.key === key || l.positions.includes(key) || l.numberedKeys.includes(key)
      )
    ) {
      return;
    }
    const player = lineup[key] ?? null;
    const priority = player
      ? getPlayerPositionPriority(player, key as Position)
      : null;
    result[key] = player
      ? { ...player, assignedPositionPriority: priority }
      : null;
  });

  return result;
};

/** Keep line_order canonical while accepting recently saved order values. */
export const normalizeFormationLineOrder = (positions: Record<string, unknown>): Record<string, unknown> =>
  Object.fromEntries(Object.entries(positions).map(([key, value]) => {
    if (!value || typeof value !== 'object' || !('line_label' in value)) return [key, value];
    const line = { ...value } as Record<string, unknown>;
    if (line.line_order == null && line.order != null) line.line_order = line.order;
    delete line.order;
    return [key, line];
  }));
